/**
 * @file GroupEditModal.js
 * @version 1.1.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-29
 * @modified 2026-06-29
 * @description Edit an existing joint account or supplier group: add / remove
 *              children, and (joint accounts only) edit each child's link scopes
 *              via a compact permissions matrix, or bulk-set every child to the
 *              same permissions in one change.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import SearchableDropdown from './SearchableDropdown';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import { addressLine1 } from '../utils/formatAddress';
import './GroupEditModal.css';

// What a joint-account link participates in (same set as the create modal).
const LINK_SCOPES = [
  { key: 'statements', label: 'Statements', icon: 'receipt_long' },
  { key: 'pricing', label: 'Pricing', icon: 'sell' },
  { key: 'credit', label: 'Credit', icon: 'credit_score' },
  { key: 'reports', label: 'Reports', icon: 'bar_chart' },
];

const post = (url, body) => fetch(`${API_URL}${url}`, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body)
});
const patch = (url, body) => fetch(`${API_URL}${url}`, {
  method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body)
});
const del = (url) => fetch(`${API_URL}${url}`, { method: 'DELETE', credentials: 'include' });

// Turn a 409 conflicts payload into a single readable message.
const conflictMessage = (data) =>
  (data.conflicts && data.conflicts.length)
    ? `${data.error || 'Cannot add'}: ` + data.conflicts.map(c => `${c.name} ${c.reason}`).join('; ')
    : (data.error || 'Operation failed');

function GroupEditModal({ type, group, options = [], onClose, onChanged }) {
  const isJoint = type === 'joint';
  const [children, setChildren] = useState(group.children || []);
  const [scopeDraft, setScopeDraft] = useState({ statements: true, reports: true, pricing: false, credit: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useSlideOutButton([busy, children.length]);

  const parentId = group.parent_id;
  const memberIds = new Set([parentId, ...children.map(c => c.id)]);
  const addOptions = options.filter(o => !memberIds.has(o.value));

  const syncParent = () => { if (onChanged) onChanged(); };

  const addChild = async (childId) => {
    if (!childId || busy) return;
    setBusy(true); setError('');
    try {
      const url = isJoint ? `/api/contacts/joint-accounts/${parentId}/children` : `/api/contacts/supplier-groups/${parentId}/add`;
      const res = await post(url, isJoint ? { childIds: [childId], scopes: scopeDraft } : { childIds: [childId] });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(conflictMessage(data)); return; }
      const opt = options.find(o => o.value === childId);
      const newChild = {
        id: childId,
        company_name: opt?.label || String(childId),
        account_no: opt?.badge || '',
        address: opt?.address,
        ...(isJoint ? { link_statements: scopeDraft.statements, link_reports: scopeDraft.reports, link_pricing: scopeDraft.pricing, link_credit: scopeDraft.credit } : {})
      };
      setChildren(prev => [...prev, newChild].sort((a, b) => (a.account_no || '').localeCompare(b.account_no || '')));
      syncParent();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const removeChild = async (childId) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const url = isJoint
        ? `/api/contacts/joint-accounts/${parentId}/children/${childId}`
        : `/api/contacts/supplier-groups/${parentId}/remove/${childId}`;
      const res = await del(url);
      if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.error || 'Failed to remove'); return; }
      setChildren(prev => prev.filter(c => c.id !== childId));
      syncParent();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const toggleChildScope = async (childId, key, value) => {
    // optimistic
    setChildren(prev => prev.map(c => c.id === childId ? { ...c, [`link_${key}`]: value } : c));
    try {
      const res = await patch(`/api/contacts/link-accounts/${childId}/scopes`, { [key]: value });
      if (!res.ok) throw new Error('patch failed');
      syncParent();
    } catch (e) {
      // revert
      setChildren(prev => prev.map(c => c.id === childId ? { ...c, [`link_${key}`]: !value } : c));
      setError('Failed to update permission');
    }
  };

  const applyBulk = async () => {
    if (busy || children.length === 0) return;
    setBusy(true); setError('');
    try {
      const res = await patch(`/api/contacts/joint-accounts/${parentId}/bulk-scopes`, scopeDraft);
      if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.error || 'Failed to apply'); return; }
      setChildren(prev => prev.map(c => ({
        ...c,
        link_statements: scopeDraft.statements, link_reports: scopeDraft.reports,
        link_pricing: scopeDraft.pricing, link_credit: scopeDraft.credit
      })));
      syncParent();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const title = isJoint ? 'Edit Joint Account' : 'Edit Supplier Group';
  const addLabel = isJoint ? 'Add child account' : 'Add child supplier';
  const childCount = `${children.length} child${children.length !== 1 ? 'ren' : ''}`;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-md group-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="group-edit-title">
            <h2 className="modal-title">{title}</h2>
            <span className="group-edit-parent">
              <span className="parent-name">{group.parent_name}</span>
              {group.parent_account_no && <span className="acc-no">{group.parent_account_no}</span>}
              {addressLine1(group.parent_address) && (
                <span className="parent-address">{addressLine1(group.parent_address)}</span>
              )}
            </span>
          </div>
          <button onClick={onClose} className="modal-close"><span className="material-symbols-rounded">close</span></button>
        </div>

        <div className="modal-body group-edit-body">
          {error && (<div className="error-message"><span className="material-symbols-rounded">error</span>{error}</div>)}

          <div className="form-group">
            <label>{addLabel}</label>
            <SearchableDropdown
              options={addOptions}
              value=""
              onChange={(v) => addChild(v)}
              placeholder="Search by # or name to add…"
              searchPlaceholder="Search by # or name..."
            />
          </div>

          {isJoint ? (
            <div className="group-edit-section">
              <div className="scope-matrix has-action">
                <div className="scope-row scope-row-head">
                  <span className="scope-cell-name"></span>
                  {LINK_SCOPES.map(s => (
                    <span key={s.key} className="scope-cell scope-col-head material-symbols-rounded" title={s.label}>{s.icon}</span>
                  ))}
                  <span className="scope-cell-action" />
                </div>

                <div className="scope-row scope-row-bulk">
                  <span className="scope-cell-name">
                    <button type="button" className="btn-round-large" title="Apply the settings (right) to every child"
                  onClick={applyBulk} disabled={busy || children.length === 0}>
                      <span className="btn-label">Apply these settings to every child account</span>
                      <div className="icon-wrapper"><span className="material-symbols-rounded">keyboard_double_arrow_right</span></div>
                    </button>
                  </span>
                  {LINK_SCOPES.map(s => (
                    <span key={s.key} className="scope-cell">
                      <TickCheckbox id={`bulk-${s.key}`} size={16} checked={scopeDraft[s.key]}
                        onChange={(e) => setScopeDraft(prev => ({ ...prev, [s.key]: e.target.checked }))}
                        ariaLabel={`Set all — ${s.label}`} title={s.label} />
                    </span>
                  ))}
                  <span className="scope-cell-action" />
                </div>

                <div className="scope-row scope-row-head">
                  <span className="scope-cell-name">
                    <label>Settings: {childCount}</label>
                  </span>
                  {LINK_SCOPES.map(s => (
                    <span key={s.key} className="scope-cell scope-col-head material-symbols-rounded" title={s.label}>{s.icon}</span>
                  ))}
                  <span className="scope-cell-action" />
                </div>

                {children.length === 0 && <div className="group-edit-empty">No children yet — add one above.</div>}
                {children.map(child => (
                  <div key={child.id} className="scope-row">
                    <span className="scope-cell-name" title={child.company_name}>
                      <span className="scope-cell-line">{child.company_name}{child.account_no && <em>{child.account_no}</em>}</span>
                      {addressLine1(child.address) && <span className="scope-cell-address">{addressLine1(child.address)}</span>}
                    </span>
                    {LINK_SCOPES.map(s => (
                      <span key={s.key} className="scope-cell">
                        <TickCheckbox id={`edit-${child.id}-${s.key}`} size={16} checked={!!child[`link_${s.key}`]}
                          onChange={(e) => toggleChildScope(child.id, s.key, e.target.checked)}
                          ariaLabel={`${child.company_name} — ${s.label}`} title={s.label} />
                      </span>
                    ))}
                    <span className="scope-cell-action">
                      <button className="btn-icon btn-danger" title="Remove from group"
                        onClick={() => removeChild(child.id)} disabled={busy}>
                        <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>close</span>
                      </button>
                    </span>
                  </div>
                ))}
              </div>

              <span className="link-scope-hint">
                “Set all” is also the default applied to any child you add. Pricing shares contract/spot
                prices; Statements &amp; Reports roll the child into the parent; invoicing always stays separate.
              </span>
            </div>
          ) : (
            <div className="group-edit-section">
              <label className="group-edit-children-label">{childCount}</label>
              {children.length === 0 && <div className="group-edit-empty">No children yet — add one above.</div>}
              <div className="group-edit-children-list">
                {children.map(child => (
                  <div key={child.id} className="child-account-row">
                    <span className="material-symbols-rounded">subdirectory_arrow_right</span>
                    <span className="child-name">
                      {child.company_name}
                      {addressLine1(child.address) && <span className="child-address"> · {addressLine1(child.address)}</span>}
                    </span>
                    {child.account_no && <span className="child-account-no">({child.account_no})</span>}
                    <button className="btn-icon btn-danger child-remove" title="Remove from group"
                      onClick={() => removeChild(child.id)} disabled={busy}>
                      <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>close</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div>
            <button data-read-safe onClick={onClose} className="btn-slideout">
              <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
              <span className="btn-label">Done</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default GroupEditModal;
